import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  EyeOff,
  Lock,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Users,
  X,
} from 'lucide-react'
import {
  adminAddMod,
  adminChangePassword,
  type FullResyncProgress,
  adminForceResync,
  adminGetTravelTimes,
  type GeometryBackup,
  adminListFlags,
  adminListGeometryBackups,
  adminListMods,
  adminLogin,
  adminRemoveMod,
  adminResolveFlag,
  adminListSuggestions,
  adminResolveSuggestion,
  adminDeleteGeometryBackup,
  adminRestoreGeometryBackup,
  adminListHiddenRoads,
  adminRestoreHiddenRoad,
  type FlagRecord,
  type HiddenRoadRecord,
  type ModRecord,
  type SuggestionRecord,
} from '@/lib/admin/adminApi'
import { AlertTriangle } from 'lucide-react'
import { checkUsernameAvailable } from '@/lib/auth/auth'
import { useRoadStore } from '@/lib/roads/store'

interface SyncStats {
  roads: number
  skipped: number
}

export default function AdminPortal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [token, setToken] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)
  const [flags, setFlags] = useState<FlagRecord[]>([])
  const [loadingFlags, setLoadingFlags] = useState(false)
  const [suggestions, setSuggestions] = useState<SuggestionRecord[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [clearingAll, setClearingAll] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [syncStats, setSyncStats] = useState<SyncStats | null>(null)
  const [resyncConfirmOpen, setResyncConfirmOpen] = useState(false)
  const [flagsOpen, setFlagsOpen] = useState(false)
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)
  const [hiddenOpen, setHiddenOpen] = useState(false)
  const [hiddenRoads, setHiddenRoads] = useState<HiddenRoadRecord[]>([])
  const [loadingHidden, setLoadingHidden] = useState(false)
  const [hiddenError, setHiddenError] = useState<string | null>(null)
  const [resyncOpen, setResyncOpen] = useState(false)
  const [backupsOpen, setBackupsOpen] = useState(false)
  const [backups, setBackups] = useState<GeometryBackup[]>([])
  const [loadingBackups, setLoadingBackups] = useState(false)
  const [backupBusyKey, setBackupBusyKey] = useState<string | null>(null)
  const [backupError, setBackupError] = useState<string | null>(null)
  const [backupNotice, setBackupNotice] = useState<string | null>(null)
  const [resyncPhase, setResyncPhase] = useState<FullResyncProgress | null>(null)
  const [timing, setTiming] = useState(false)
  const [timeStats, setTimeStats] = useState<string | null>(null)
  const [timeError, setTimeError] = useState<string | null>(null)
  const roads = useRoadStore((s) => s.roads)
  const selectedId = useRoadStore((s) => s.selectedId)
  const selectRoad = useRoadStore((s) => s.selectRoad)
  const reloadHiddenRoadIds = useRoadStore((s) => s.loadHiddenRoads)
  // Anchored mode: a flagged road was clicked from the panel — the portal docks
  // to the left edge (backdrop removed so the map stays interactive) until the
  // selected road is deselected on the map.
  const [anchored, setAnchored] = useState(false)
  const [pwCurrent, setPwCurrent] = useState('')
  const [pwNew, setPwNew] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [pwError, setPwError] = useState<string | null>(null)
  const [pwSuccess, setPwSuccess] = useState(false)
  const [pwSaving, setPwSaving] = useState(false)
  const [pwOpen, setPwOpen] = useState(false)
  const [modsOpen, setModsOpen] = useState(false)
  const [mods, setMods] = useState<ModRecord[]>([])
  const [loadingMods, setLoadingMods] = useState(false)
  const [modUsername, setModUsername] = useState('')
  const [modUsernameState, setModUsernameState] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle')
  const [addingMod, setAddingMod] = useState(false)
  const [modError, setModError] = useState<string | null>(null)
  const [modSuccess, setModSuccess] = useState<string | null>(null)
  const [removingModId, setRemovingModId] = useState<string | null>(null)
  const modTimer = useRef<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dismissTimer = useRef<number | null>(null)
  const pwTimer = useRef<number | null>(null)

  const loadFlags = useCallback(async (t: string) => {
    setLoadingFlags(true)
    try {
      const data = await adminListFlags(t)
      setFlags(data)
    } catch {
      setFlags([])
    } finally {
      setLoadingFlags(false)
    }
  }, [])

  const loadBackups = useCallback(async (t: string) => {
    setLoadingBackups(true)
    try {
      const data = await adminListGeometryBackups(t)
      setBackups(data)
    } catch {
      setBackups([])
    } finally {
      setLoadingBackups(false)
    }
  }, [])

  const loadSuggestions = useCallback(async (t: string) => {
    setLoadingSuggestions(true)
    setSuggestionsError(null)
    try {
      const data = await adminListSuggestions(t)
      setSuggestions(data.filter((s) => s.status === 'pending'))
    } catch {
      setSuggestions([])
      setSuggestionsError('Could not load suggestions.')
    } finally {
      setLoadingSuggestions(false)
    }
  }, [])

  const loadHidden = useCallback(async (t: string) => {
    setLoadingHidden(true)
    setHiddenError(null)
    try {
      const data = await adminListHiddenRoads(t)
      setHiddenRoads(
        [...data].sort((a, b) => (a.review_at ?? '').localeCompare(b.review_at ?? '')),
      )
    } catch {
      setHiddenRoads([])
      setHiddenError('Could not load hidden roads.')
    } finally {
      setLoadingHidden(false)
    }
  }, [])

  useEffect(() => {
    if (open && token) {
      void loadFlags(token)
      void loadSuggestions(token)
      void loadHidden(token)
    }
  }, [open, token, loadFlags, loadSuggestions, loadHidden])

  const resolveSuggestion = useCallback(
    async (id: string, accepted: boolean) => {
      if (!token || busyId) return
      setBusyId(id)
      try {
        await adminResolveSuggestion(token, id, accepted)
        setSuggestions((prev) => prev.filter((s) => s.id !== id))
      } finally {
        setBusyId(null)
      }
    },
    [token, busyId],
  )

  useEffect(() => {
    if (open && token && suggestionsOpen) void loadSuggestions(token)
  }, [open, token, suggestionsOpen, loadSuggestions])

  useEffect(() => {
    if (open && token && hiddenOpen) void loadHidden(token)
  }, [open, token, hiddenOpen, loadHidden])

  const restoreHidden = useCallback(
    async (roadId: string) => {
      if (!token || busyId) return
      setBusyId(roadId)
      try {
        await adminRestoreHiddenRoad(token, roadId)
        setHiddenRoads((prev) => prev.filter((h) => h.road_id !== roadId))
        await reloadHiddenRoadIds()
        // Re-pull the road list past the 60s refresh throttle so the restored
        // road is back on the map and in search immediately.
        void useRoadStore.getState().fetchRoads(true)
        // Re-select the restored road so it opens in the road detail panel and
        // the portal docks to the left edge, same as clicking a flagged road.
        setAnchored(true)
        selectRoad(roadId)
      } finally {
        setBusyId(null)
      }
    },
    [token, busyId, reloadHiddenRoadIds],
  )

  useEffect(() => {
    if (open && token && backupsOpen) void loadBackups(token)
  }, [open, token, backupsOpen, loadBackups])

  useEffect(() => {
    if (!open) {
      setToken(null)
      setFlags([])
      setSuggestions([])
      setSuggestionsError(null)
      setFlagsOpen(false)
      setSuggestionsOpen(false)
      setHiddenOpen(false)
      setHiddenRoads([])
      setHiddenError(null)
      setResyncOpen(false)
      setBackupsOpen(false)
      setBackups([])
      setBackupError(null)
      setBackupNotice(null)
      setAnchored(false)
    }
  }, [open])

  // Anchored portal follows the map's selection: when the selected flagged road
  // is deselected (clicked away / Escape / X on the road panel), the portal
  // closes. Selecting a different flagged road from the list keeps it open.
  // A deselection caused by HIDING the selected road keeps the portal open —
  // the road is gone from the map, but moderation work continues here.
  const hiddenRoadIds = useRoadStore((s) => s.hiddenRoadIds)
  const prevSelectedRef = useRef<string | null>(null)
  useEffect(() => {
    const wasHidden =
      prevSelectedRef.current !== null && hiddenRoadIds.has(prevSelectedRef.current)
    prevSelectedRef.current = selectedId
    if (anchored && !selectedId && !wasHidden) onClose()
  }, [anchored, selectedId, hiddenRoadIds, onClose])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const loadMods = useCallback(async (t: string) => {
    setLoadingMods(true)
    try {
      const data = await adminListMods(t)
      setMods(data)
    } catch {
      setMods([])
    } finally {
      setLoadingMods(false)
    }
  }, [])

  useEffect(() => {
    if (open && token && modsOpen) void loadMods(token)
  }, [open, token, modsOpen, loadMods])

  // Live username validity: fires ~300ms after typing stops, checks the same
  // "username exists" lookup used by the signup flow (inverted).
  useEffect(() => {
    if (modTimer.current) window.clearTimeout(modTimer.current)
    setModError(null)
    const name = modUsername.trim()
    if (!modsOpen || !name) {
      setModUsernameState('idle')
      return
    }
    setModUsernameState('checking')
    modTimer.current = window.setTimeout(() => {
      void checkUsernameAvailable(name).then((available) => {
        setModUsernameState(available ? 'invalid' : 'valid')
      })
    }, 300)
  }, [modUsername, modsOpen])

  useEffect(() => {
    return () => {
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current)
      if (pwTimer.current) window.clearTimeout(pwTimer.current)
      if (modTimer.current) window.clearTimeout(modTimer.current)
    }
  }, [])

  // Reset the Mods section state when the portal closes (token is cleared too).
  useEffect(() => {
    if (!open) {
      setModsOpen(false)
      setMods([])
      setModUsername('')
      setModUsernameState('idle')
      setModError(null)
      setModSuccess(null)
    }
  }, [open])

  const flagsListRef = useRef<HTMLDivElement | null>(null)
  const lastScrolledKey = useRef<string | null>(null)
  const selectedGroupKey = (() => {
    if (!selectedId) return null
    const road = roads.find((r) => r.id === selectedId)
    if (!road) return null
    const name = road.name.trim().toLowerCase()
    const f = flags.find(
      (x) =>
        x.road_id === selectedId ||
        (x.road_name != null && x.road_name.trim().toLowerCase() === name),
    )
    return f ? `${f.road_name ?? f.road_id ?? f.id}||${f.reason ?? ''}` : null
  })()
  useEffect(() => {
    if (!open || !selectedGroupKey || lastScrolledKey.current === selectedGroupKey) return
    lastScrolledKey.current = selectedGroupKey
    const el = flagsListRef.current?.querySelector<HTMLElement>(
      `[data-flag-key="${CSS.escape(selectedGroupKey)}"]`,
    )
    el?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [open, selectedGroupKey])

  if (!open) return null

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError(null)
    try {
      const { token: t } = await adminLogin(password)
      setToken(t)
      setPassword('')
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Login failed')
    }
  }

  const clearFlag = async (ids: string[], key: string) => {
    if (!token) return
    setBusyId(key)
    try {
      await Promise.all(ids.map((id) => adminResolveFlag(token, id, true)))
      setFlags((f) => f.filter((x) => !ids.includes(x.id)))
    } catch {
      /* keep row on failure */
    } finally {
      setBusyId(null)
    }
  }

  const clearAll = async () => {
    if (!token) return
    setClearingAll(true)
    try {
      const open = flags.filter((f) => f.status !== 'resolved')
      await Promise.all(open.map((f) => adminResolveFlag(token, f.id, true)))
      setFlags((f) => f.filter((x) => x.status === 'resolved'))
    } catch {
      /* partial failures ignored */
    } finally {
      setClearingAll(false)
    }
  }

  const resync = async () => {
    if (!token) return
    setSyncing(true)
    setSyncError(null)
    setSyncStats(null)
    setResyncPhase(null)
    try {
      const stats = await adminForceResync(token, (p) => setResyncPhase(p))
      setSyncStats(stats)
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current)
      dismissTimer.current = window.setTimeout(() => setSyncStats(null), 6000)
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Resync failed')
    } finally {
      setSyncing(false)
      setResyncPhase(null)
    }
  }

  const restoreBackup = async (b: GeometryBackup) => {
    if (!token) return
    const key = `restore-${b.backed_up_at}`
    setBackupBusyKey(key)
    setBackupError(null)
    setBackupNotice(null)
    try {
      const restored = await adminRestoreGeometryBackup(token, b.backed_up_at)
      setBackupNotice(`Restored ${restored} polylines into the live geometry cache`)
      await loadBackups(token)
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current)
      dismissTimer.current = window.setTimeout(() => setBackupNotice(null), 6000)
    } catch (err) {
      setBackupError(err instanceof Error ? err.message : 'Restore failed')
    } finally {
      setBackupBusyKey(null)
    }
  }

  const deleteBackup = async (b: GeometryBackup) => {
    if (!token) return
    const key = `delete-${b.backed_up_at}`
    setBackupBusyKey(key)
    setBackupError(null)
    setBackupNotice(null)
    try {
      await adminDeleteGeometryBackup(token, b.backed_up_at)
      setBackups((prev) => prev.filter((x) => x.backed_up_at !== b.backed_up_at))
    } catch (err) {
      setBackupError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setBackupBusyKey(null)
    }
  }

  const getTravelTimes = async () => {
    if (!token) return
    setTiming(true)
    setTimeError(null)
    setTimeStats(null)
    try {
      const stats = await adminGetTravelTimes(roads)
      setTimeStats(
        `Updated ${stats.updated} roads` +
          (stats.failed > 0 ? ` · ${stats.failed} unroutable` : '') +
          (stats.missing > 0 ? ` · ${stats.missing} still missing` : ''),
      )
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current)
      dismissTimer.current = window.setTimeout(() => setTimeStats(null), 6000)
    } catch (err) {
      setTimeError(err instanceof Error ? err.message : 'Travel time lookup failed')
    } finally {
      setTiming(false)
    }
  }

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwError(null)
    setPwSuccess(false)
    if (!token) return
    if (!pwCurrent || !pwNew || !pwConfirm) {
      setPwError('All fields are required')
      return
    }
    if (pwNew !== pwConfirm) {
      setPwError('New passwords do not match')
      return
    }
    if (pwNew.length < 8) {
      setPwError('New password must be at least 8 characters')
      return
    }
    setPwSaving(true)
    try {
      await adminChangePassword(token, pwCurrent, pwNew)
      setPwCurrent('')
      setPwNew('')
      setPwConfirm('')
      setPwSuccess(true)
      if (pwTimer.current) window.clearTimeout(pwTimer.current)
      pwTimer.current = window.setTimeout(() => setPwSuccess(false), 4000)
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Password change failed')
    } finally {
      setPwSaving(false)
    }
  }

  const addMod = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || modUsernameState !== 'valid') return
    setAddingMod(true)
    setModError(null)
    setModSuccess(null)
    try {
      await adminAddMod(token, modUsername.trim())
      setModSuccess(`${modUsername.trim()} is now a moderator`)
      setModUsername('')
      setModUsernameState('idle')
      await loadMods(token)
      if (modTimer.current) window.clearTimeout(modTimer.current)
      modTimer.current = window.setTimeout(() => setModSuccess(null), 4000)
    } catch (err) {
      setModError(err instanceof Error ? err.message : 'Could not add moderator')
    } finally {
      setAddingMod(false)
    }
  }

  const removeMod = async (m: ModRecord) => {
    if (!token) return
    setRemovingModId(m.user_id)
    setModError(null)
    try {
      await adminRemoveMod(token, m.user_id)
      setMods((prev) => prev.filter((x) => x.user_id !== m.user_id))
    } catch (err) {
      setModError(err instanceof Error ? err.message : 'Could not remove moderator')
    } finally {
      setRemovingModId(null)
    }
  }

  const exportCsv = () => {
    const rows: string[][] = [
      ['road_name', 'road_id', 'reason', 'status', 'flagged_at'],
      ...orderedOpenFlags
        .map((f) => [
          f.road_name ?? '',
          f.road_id ?? '',
          f.reason ?? '',
          f.status ?? '',
          f.created_at ?? '',
        ]),
    ]
    const csv = rows
      .map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `roadratings-flags-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const openFlags = flags.filter((f) => f.status !== 'resolved')

  // Sort order: (1) user-flagged roads, most recent first; (2) system-flagged
  // roads that have ratings, A-Z; (3) system-flagged roads without ratings, A-Z.
  // System flags are the ones auto-inserted by the pipeline with known reasons.
  const SYSTEM_REASONS = new Set([
    'Check deviated routing',
    'Check/Update Route Waypoints',
    'no_geometry',
  ])
  const isSystemFlag = (f: FlagRecord) => SYSTEM_REASONS.has(f.reason ?? '')

  // ratings is a fixed-shape object with nullable values — a road is "rated"
  // only if at least one rating value is actually present.
  const hasRatings = (f: FlagRecord) => {
    if (!f.road_id) return false
    const r = roads.find((x) => x.id === f.road_id)?.ratings
    if (!r) return false
    return [r.overall, r.fun, r.pavement, r.traffic, r.dirt].some((v) => v != null)
  }

  const tierOf = (f: FlagRecord): 0 | 1 | 2 =>
    isSystemFlag(f) ? (hasRatings(f) ? 1 : 2) : 0

  const orderedOpenFlags = [...openFlags].sort((a, b) => {
    const ta = tierOf(a)
    const tb = tierOf(b)
    if (ta !== tb) return ta - tb
    if (ta === 0) return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    return (a.road_name ?? '').localeCompare(b.road_name ?? '')
  })

  const grouped: {
    key: string
    label: string
    reason: string
    date: string
    ids: string[]
    tier: 0 | 1 | 2
    flaggerEmail: string | null
  }[] = []
  for (const f of orderedOpenFlags) {
    const label = f.road_name ?? f.road_id ?? f.id
    const reason = f.reason ?? ''
    const key = `${label}||${reason}`
    const existing = grouped.find((g) => g.key === key)
    if (existing) {
      existing.ids.push(f.id)
      if (new Date(f.created_at) > new Date(existing.date)) existing.date = f.created_at
      if (f.flagger_email) existing.flaggerEmail = f.flagger_email
    } else {
      grouped.push({
        key,
        label,
        reason,
        date: f.created_at,
        ids: [f.id],
        tier: tierOf(f),
        flaggerEmail: f.flagger_email ?? null,
      })
    }
  }
  grouped.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier
    if (a.tier === 0) return new Date(b.date).getTime() - new Date(a.date).getTime()
    const byName = a.label.localeCompare(b.label)
    return byName !== 0 ? byName : a.reason.localeCompare(b.reason)
  })

  return (
    <div
      className={
        anchored
          ? 'pointer-events-none fixed inset-0 z-[100] flex items-stretch justify-start'
          : 'pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4'
      }
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      {resyncConfirmOpen && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-black/70 p-4">
          <div className="w-full max-w-xs rounded-xl border border-amber-500/40 bg-zinc-950 p-5 text-white shadow-2xl">
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle size={18} className="shrink-0 text-amber-400" />
              <h3 className="text-sm font-semibold">Confirm Force Resync</h3>
            </div>
            <p className="mb-5 text-xs leading-relaxed text-white/70">
              This will wipe all road data and force a fresh import and road geometry
              calculation for ALL roads (resource intensive). Confirm?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setResyncConfirmOpen(false)
                  void resync()
                }}
                disabled={syncing}
                className="h-9 flex-1 rounded-lg bg-orange-600 text-xs font-semibold text-white hover:bg-orange-500 transition-colors disabled:opacity-50"
              >
                {syncing ? 'Starting…' : 'Yes'}
              </button>
              <button
                onClick={() => setResyncConfirmOpen(false)}
                disabled={syncing}
                className="h-9 flex-1 rounded-lg border border-white/15 bg-white/[0.04] text-xs font-semibold text-white/70 hover:border-white/40 hover:text-white transition-colors disabled:opacity-50"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        data-admin-portal="true"
        className={
          anchored
            ? 'pointer-events-auto relative flex h-full w-1/2 max-w-[14rem] flex-col overflow-y-auto rounded-none border-y-0 border-l-0 border-r border-white/10 bg-zinc-950 p-3 text-white shadow-2xl'
            : 'pointer-events-auto relative flex max-h-[85vh] w-full max-w-md flex-col overflow-y-auto rounded-xl border border-white/10 bg-zinc-950 p-5 text-white shadow-2xl'
        }
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-lg p-1.5 text-white/40 hover:bg-white/10 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>

        {!token ? (
          <form onSubmit={signIn} className="space-y-4 pt-8">
            <div className="flex items-center gap-2">
              <Lock size={16} className="text-[#EA580C]" />
              <h2 className="text-sm font-semibold tracking-wide">Admin Portal</h2>
            </div>
            <input
              ref={inputRef}
              autoFocus
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="h-10 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#EA580C]/70"
            />
            {authError && <p className="text-xs text-red-400">{authError}</p>}
            <button
              type="submit"
              className="h-10 w-full rounded-lg bg-orange-600 text-sm font-semibold text-white hover:bg-orange-500 transition-colors"
            >
              Sign In
            </button>
          </form>
        ) : (
          <div className="space-y-5 pt-8">
            <section>
              <button
                onClick={() => setFlagsOpen((v) => !v)}
                className="mb-2 flex h-9 w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 hover:border-[#EA580C]/50 transition-colors"
              >
                <span className="flex items-center gap-2">
                  {flagsOpen ? (
                    <ChevronDown size={14} className="text-white/50" />
                  ) : (
                    <ChevronRight size={14} className="text-white/50" />
                  )}
                  <h3 className="text-[11px] font-mono uppercase tracking-widest text-white/50">
                    Flagged roads
                  </h3>
                </span>
                <span className="rounded-full bg-[#EA580C]/15 border border-[#EA580C]/40 px-2 py-0.5 text-[10px] font-mono text-orange-400">
                  {openFlags.length} open
                </span>
              </button>

              {flagsOpen && (
                <>
              {loadingFlags && <p className="text-xs text-white/40 py-2">Loading flags…</p>}

              {!loadingFlags && openFlags.length === 0 && (
                <p className="text-xs text-white/40 py-2">No flagged roads 🎉</p>
              )}

              <div ref={flagsListRef} className="space-y-1">
                {grouped.map((g) => (
                  <div
                    key={g.key}
                    data-flag-key={g.key}
                    onClick={() => {
                      const label = g.label.trim().toLowerCase()
                      const match = roads.find(
                        (r) =>
                          g.ids.includes(r.id) ||
                          (label !== '' && r.name.trim().toLowerCase() === label),
                      )
                      if (!match) return
                      setAnchored(true)
                      selectRoad(match.id)
                    }}
                    className={
                      selectedGroupKey === g.key
                        ? 'flex items-start gap-2 rounded-lg border border-amber-400 bg-amber-400/10 px-2.5 py-1.5 cursor-pointer'
                        : g.tier === 0
                          ? 'flex items-start gap-2 rounded-lg bg-white/[0.03] border border-yellow-400/70 px-2.5 py-1.5 cursor-pointer hover:border-yellow-300 hover:bg-white/[0.06] transition-colors'
                          : 'flex items-start gap-2 rounded-lg bg-white/[0.03] border border-white/5 px-2.5 py-1.5 cursor-pointer hover:border-[#EA580C]/60 hover:bg-white/[0.06] transition-colors'
                    }
                  >
                    <span className="min-w-0 flex-1 break-words whitespace-normal font-mono text-sm text-white/85">
                      {g.label}
                      <span className="text-white/35"> — {g.reason}</span>
                      <span className="text-white/25">
                        {' '}·{' '}
                        {new Date(g.date).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      {g.tier === 0 && (
                        <span className="block text-[11px] text-white/40">
                          {g.flaggerEmail ?? 'Anon'}
                        </span>
                      )}
                    </span>
                    {g.ids.length > 1 && (
                      <span className="shrink-0 pt-0.5 font-mono text-[11px] text-white">
                        x {g.ids.length}
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        clearFlag(g.ids, g.key)
                      }}
                      disabled={busyId === g.key}
                      className="shrink-0 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-white/60 hover:border-red-500/60 hover:text-red-400 transition-colors disabled:opacity-40"
                    >
                      {busyId === g.key ? '…' : 'Clear'}
                    </button>
                  </div>
                ))}
              </div>

              {openFlags.length > 0 && (
                <button
                  onClick={clearAll}
                  disabled={clearingAll}
                  className="mt-2.5 h-8 w-full rounded-lg bg-red-600/90 text-xs font-semibold text-white hover:bg-red-500 transition-colors disabled:opacity-50"
                >
                  {clearingAll ? 'Clearing…' : 'Clear All'}
                </button>
              )}

              {openFlags.length > 0 && (
                <button
                  onClick={exportCsv}
                  className="mt-2 flex h-8 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] text-xs font-semibold text-white/70 hover:border-[#EA580C]/60 hover:text-white transition-colors"
                >
                  <Download size={13} />
                  Export CSV
                </button>
              )}
                </>
              )}
            </section>

            <section className="border-t border-white/10 pt-4">
              <button
                onClick={() => setSuggestionsOpen((v) => !v)}
                className="mb-2 flex h-9 w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 hover:border-[#EA580C]/50 transition-colors"
              >
                <span className="flex items-center gap-2">
                  {suggestionsOpen ? (
                    <ChevronDown size={14} className="text-white/50" />
                  ) : (
                    <ChevronRight size={14} className="text-white/50" />
                  )}
                  <h3 className="text-[11px] font-mono uppercase tracking-widest text-white/50">
                    Suggested roads
                  </h3>
                </span>
                <span className="rounded-full bg-[#EA580C]/15 border border-[#EA580C]/40 px-2 py-0.5 text-[10px] font-mono text-orange-400">
                  {suggestions.filter((s) => s.status === 'pending').length} pending
                </span>
              </button>

              {suggestionsOpen && (
                <>
                  {loadingSuggestions && (
                    <p className="text-xs text-white/40 py-2">Loading suggestions…</p>
                  )}
                  {suggestionsError && (
                    <p className="text-xs text-red-400 py-1">{suggestionsError}</p>
                  )}
                  {!loadingSuggestions && !suggestionsError && suggestions.length === 0 && (
                    <p className="text-xs text-white/40 py-2">No suggestions yet</p>
                  )}

                  <div className="space-y-1.5">
                    {suggestions.map((s) => (
                      <div
                        key={s.id}
                        className={
                          s.status === 'pending'
                            ? 'rounded-lg bg-white/[0.03] border border-white/10 px-2.5 py-2'
                            : 'rounded-lg bg-white/[0.02] border border-white/5 px-2.5 py-2 opacity-60'
                        }
                      >
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1 space-y-0.5">
                            <p className="break-words whitespace-normal font-mono text-[13px] text-white/85">
                              {s.email ?? 'Anonymous'}
                            </p>
                            {s.notes && (
                              <p className="break-words whitespace-normal text-[12px] text-white/55">
                                {s.notes}
                              </p>
                            )}
                            <a
                              href={s.google_maps_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block break-all text-[11px] text-sky-400 hover:text-sky-300 underline underline-offset-2"
                            >
                              {s.google_maps_url}
                            </a>
                            <p className="font-mono text-[10px] text-white/30">
                              {new Date(s.created_at).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                              })}
                              {s.status !== 'pending' ? ` · ${s.status}` : ''}
                            </p>
                          </div>
                          {s.status === 'pending' && (
                            <div className="flex shrink-0 gap-1.5 pt-0.5">
                              <button
                                onClick={() => resolveSuggestion(s.id, true)}
                                disabled={busyId === s.id}
                                aria-label="Accept suggestion"
                                className="rounded-md border border-emerald-500/50 p-1.5 text-emerald-400 hover:bg-emerald-500/15 transition-colors disabled:opacity-40"
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={() => resolveSuggestion(s.id, false)}
                                disabled={busyId === s.id}
                                aria-label="Decline suggestion"
                                className="rounded-md border border-red-500/50 p-1.5 text-red-400 hover:bg-red-500/15 transition-colors disabled:opacity-40"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

            <section className="border-t border-white/10 pt-4">
              <button
                onClick={() => setHiddenOpen((v) => !v)}
                className="mb-2 flex h-9 w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 hover:border-[#EA580C]/50 transition-colors"
              >
                <span className="flex items-center gap-2">
                  {hiddenOpen ? (
                    <ChevronDown size={14} className="text-white/50" />
                  ) : (
                    <ChevronRight size={14} className="text-white/50" />
                  )}
                  <h3 className="text-[11px] font-mono uppercase tracking-widest text-white/50">
                    Hidden roads
                  </h3>
                </span>
                <span className="flex items-center gap-1.5">
                  {loadingHidden && (
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  )}
                  <span className="rounded-full bg-[#EA580C]/15 border border-[#EA580C]/40 px-2 py-0.5 text-[10px] font-mono text-orange-400">
                    {hiddenRoads.length} hidden
                  </span>
                </span>
              </button>

              {hiddenOpen && (
                <>
                  {hiddenError && <p className="text-xs text-red-400 py-1">{hiddenError}</p>}
                  {!loadingHidden && !hiddenError && hiddenRoads.length === 0 && (
                    <p className="text-xs text-white/40 py-2">No roads are currently hidden</p>
                  )}

                  <div className="space-y-1.5">
                    {hiddenRoads.map((h) => (
                      <div
                        key={h.id}
                        className="rounded-lg bg-white/[0.03] border border-white/10 px-2.5 py-2"
                      >
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1 space-y-0.5">
                            <p className="break-words whitespace-normal font-mono text-[13px] text-white/85">
                              {h.road_name ?? h.road_id}
                            </p>
                            <p className="break-words whitespace-normal text-[12px] text-white/55">
                              {h.reason}
                            </p>
                            <p className="font-mono text-[10px] text-white/30">
                              Hidden by {h.hidden_by_email ?? 'Unknown'} ·{' '}
                              <span className="text-amber-400">
                                Review{' '}
                                {new Date(h.review_at + 'T00:00:00').toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>
                            </p>
                          </div>
                          <button
                            onClick={() => restoreHidden(h.road_id)}
                            disabled={busyId === h.road_id}
                            aria-label={`Restore ${h.road_name ?? h.road_id}`}
                            className="shrink-0 rounded-md border border-emerald-500/50 px-2 py-1 text-[11px] text-emerald-400 hover:bg-emerald-500/15 transition-colors disabled:opacity-40"
                          >
                            {busyId === h.road_id ? '…' : 'Restore'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

            <section className="border-t border-white/10 pt-4">
              <button
                onClick={() => setResyncOpen((v) => !v)}
                className="mb-2 flex h-9 w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 hover:border-[#EA580C]/50 transition-colors"
              >
                <span className="flex items-center gap-2">
                  {resyncOpen ? (
                    <ChevronDown size={14} className="text-white/50" />
                  ) : (
                    <ChevronRight size={14} className="text-white/50" />
                  )}
                  <h3 className="text-[11px] font-mono uppercase tracking-widest text-white/50">
                    Data resync
                  </h3>
                </span>
                {syncing && (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                )}
              </button>

              {resyncOpen && (
                <>
              <button
                onClick={() => setResyncConfirmOpen(true)}
                disabled={syncing}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-orange-600 text-sm font-semibold text-white hover:bg-orange-500 transition-colors disabled:opacity-50"
              >
                {syncing ? (
                  <>
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Full rebuild in progress…
                  </>
                ) : (
                  <>
                    <RefreshCw size={15} />
                    Force Resync
                  </>
                )}
              </button>
              {syncing && resyncPhase && (
                <p className="mt-2 text-xs text-amber-300">
                  {resyncPhase.phase === 'sheet' && 'Pulling Google Sheet…'}
                  {resyncPhase.phase === 'snapshot' && 'Saving road data…'}
                  {resyncPhase.phase === 'clear' && 'Clearing cached geometry…'}
                  {resyncPhase.phase === 'routing' &&
                    `Re-routing roads ${resyncPhase.current}/${resyncPhase.total}…`}
                </p>
              )}
              {syncStats && (
                <p className="mt-2 text-xs text-emerald-400">
                  Rebuilt {syncStats.roads} roads from fresh routing
                  {typeof syncStats.geometryCleared === 'number'
                    ? ` · ${syncStats.geometryCleared} old polylines backed up & cleared`
                    : ''}
                  {syncStats.skipped > 0 ? ` · ${syncStats.skipped} unroutable/skipped` : ''}
                  {` · audits run — check flags list`}
                </p>
              )}
              {syncError && <p className="mt-2 text-xs text-red-400">{syncError}</p>}

              <button
                onClick={getTravelTimes}
                disabled={timing}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] text-sm font-semibold text-white/80 hover:border-[#EA580C]/60 hover:text-white transition-colors disabled:opacity-50"
              >
                {timing ? (
                  <>
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Routing roads through OSRM…
                  </>
                ) : (
                  <>
                    <Clock size={15} />
                    Force Get Travel Times
                  </>
                )}
              </button>
              <p className="mt-1 text-[11px] text-white/35">
                Runs OSRM routing for every road without a travel time. Cached times are kept.
              </p>
              {timeStats && <p className="text-xs text-emerald-400">{timeStats}</p>}
              {timeError && <p className="text-xs text-red-400">{timeError}</p>}

              <div className="mt-4 border-t border-white/5 pt-3">
                <button
                  onClick={() => setBackupsOpen((v) => !v)}
                  className="mb-1.5 flex h-8 w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 hover:border-white/25 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    {backupsOpen ? (
                      <ChevronDown size={13} className="text-white/40" />
                    ) : (
                      <ChevronRight size={13} className="text-white/40" />
                    )}
                    <span className="text-[11px] font-mono uppercase tracking-widest text-white/50">
                      Road data backups
                    </span>
                  </span>
                  <span className="font-mono text-[10px] text-white/40">{backups.length}</span>
                </button>

                {backupsOpen && (
                  <div className="space-y-1">
                    {loadingBackups && (
                      <p className="text-xs text-white/40 py-1">Loading backups…</p>
                    )}
                    {!loadingBackups && backups.length === 0 && (
                      <p className="text-xs text-white/40 py-1">
                        No backups yet — one is saved automatically each Force Resync
                      </p>
                    )}
                    {backups.map((b) => (
                      <div
                        key={b.backed_up_at}
                        className="flex items-center gap-2 rounded-lg bg-white/[0.03] border border-white/5 px-2.5 py-1.5"
                      >
                        <span className="min-w-0 flex-1 break-words whitespace-normal font-mono text-[11px] text-white/80">
                          {new Date(b.backed_up_at).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          <span className="text-white/35"> · {b.road_count} roads</span>
                        </span>
                        <button
                          onClick={() => restoreBackup(b)}
                          disabled={backupBusyKey !== null}
                          className="shrink-0 flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-white/60 hover:border-emerald-500/60 hover:text-emerald-400 transition-colors disabled:opacity-40"
                        >
                          <RotateCcw size={11} />
                          {backupBusyKey === `restore-${b.backed_up_at}` ? '…' : 'Restore'}
                        </button>
                        <button
                          onClick={() => deleteBackup(b)}
                          disabled={backupBusyKey !== null}
                          className="shrink-0 flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-white/60 hover:border-red-500/60 hover:text-red-400 transition-colors disabled:opacity-40"
                        >
                          <Trash2 size={11} />
                          {backupBusyKey === `delete-${b.backed_up_at}` ? '…' : 'Delete'}
                        </button>
                      </div>
                    ))}
                    {backupError && <p className="text-xs text-red-400">{backupError}</p>}
                    {backupNotice && <p className="text-xs text-emerald-400">{backupNotice}</p>}
                  </div>
                )}
              </div>
                </>
              )}
            </section>

            <section className="border-t border-white/10 pt-4">
              <button
                onClick={() => setModsOpen((v) => !v)}
                className="mb-2 flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] text-sm font-semibold text-white/80 hover:border-[#EA580C]/60 hover:text-white transition-colors"
              >
                <Users size={15} />
                Mods
              </button>

              {modsOpen && (
                <div className="space-y-3">
                  <form onSubmit={addMod} className="space-y-2">
                    <input
                      type="text"
                      value={modUsername}
                      onChange={(e) => setModUsername(e.target.value)}
                      placeholder="Username to promote"
                      className="h-9 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#EA580C]/70"
                    />
                    {modUsernameState === 'checking' && (
                      <p className="text-[11px] text-white/40">Checking username…</p>
                    )}
                    {modUsernameState === 'invalid' && modUsername.trim() !== '' && (
                      <p className="text-[11px] text-red-400">
                        No account found with that username
                      </p>
                    )}
                    {modUsernameState === 'valid' && (
                      <p className="text-[11px] text-emerald-400">Account found</p>
                    )}
                    {modError && <p className="text-xs text-red-400">{modError}</p>}
                    {modSuccess && <p className="text-xs text-emerald-400">{modSuccess}</p>}
                    <button
                      type="submit"
                      disabled={modUsernameState !== 'valid' || addingMod}
                      className="flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-orange-600 text-xs font-semibold text-white hover:bg-orange-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ShieldCheck size={13} />
                      {addingMod ? 'Adding…' : 'Make Mod'}
                    </button>
                  </form>

                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-widest text-white/50 mb-1.5">
                      Current moderators
                    </p>
                    {loadingMods ? (
                      <p className="text-xs text-white/40 py-1">Loading…</p>
                    ) : mods.length === 0 ? (
                      <p className="text-xs text-white/40 py-1">No moderators yet</p>
                    ) : (
                      <div className="space-y-1">
                        {mods.map((m) => (
                          <div
                            key={m.user_id}
                            className="flex items-center gap-2 rounded-lg bg-white/[0.03] border border-white/5 px-2.5 py-1.5"
                          >
                            <span className="min-w-0 flex-1 truncate font-mono text-sm text-white/85">
                              {m.username ?? m.user_id}
                            </span>
                            <button
                              onClick={() => removeMod(m)}
                              disabled={removingModId === m.user_id}
                              className="shrink-0 flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-white/60 hover:border-red-500/60 hover:text-red-400 transition-colors disabled:opacity-40"
                            >
                              <ShieldOff size={11} />
                              {removingModId === m.user_id ? '…' : 'Remove Mod'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>

            <section className="border-t border-white/10 pt-4">
              <h3 className="text-[11px] font-mono uppercase tracking-widest text-white/50 mb-2">
                Change password
              </h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!pwOpen) {
                    setPwOpen(true)
                    setPwError(null)
                    setPwSuccess(false)
                    return
                  }
                  changePassword(e)
                }}
                className="space-y-2"
              >
                {pwOpen && (
                  <>
                    <input
                      type="password"
                      value={pwCurrent}
                      onChange={(e) => setPwCurrent(e.target.value)}
                      placeholder="Current password"
                      autoFocus
                      className="h-9 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#EA580C]/70"
                    />
                    <input
                      type="password"
                      value={pwNew}
                      onChange={(e) => setPwNew(e.target.value)}
                      placeholder="New password"
                      className="h-9 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#EA580C]/70"
                    />
                    <input
                      type="password"
                      value={pwConfirm}
                      onChange={(e) => setPwConfirm(e.target.value)}
                      placeholder="Confirm new password"
                      className="h-9 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#EA580C]/70"
                    />
                  </>
                )}
                {pwError && <p className="text-xs text-red-400">{pwError}</p>}
                {pwSuccess && <p className="text-xs text-emerald-400">Password updated</p>}
                <button
                  type="submit"
                  disabled={pwSaving}
                  className="h-9 w-full rounded-lg bg-orange-600 text-xs font-semibold text-white hover:bg-orange-500 transition-colors disabled:opacity-50"
                >
                  {pwSaving ? 'Updating…' : pwOpen ? 'Save new password' : 'Change password'}
                </button>
              </form>
            </section>
          </div>
        )}
      </div>
    </div>
  )
}
